import React from "react";
import zoomSdk from "@zoom/appssdk";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";

import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Icon from "@amzn/meridian/esm/components/icon";
import Link from "@amzn/meridian/esm/components/link";
import Theme from "@amzn/meridian/esm/components/theme";

import alertFilledTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import errorKnockout from "@amzn/meridian-tokens/base/icon/error-knockout";
import lineIcon from "../../../images/line-summary-sections.svg";
import LogManager from "../../utils/LogManager";

/**
 * SummaryBodyInDetailPage Component
 *
 * Renders the detailed body content of a meeting summary in the summary detail page.
 * This component encapsulates all conditional rendering logic based on `summary.summaryType`,
 * such as displaying warnings or error messages, or the full summary content including:
 * - Narrative summary
 * - Decisions (as bullet points)
 * - Next Steps (as bullet points)
 * - A footer note about AI-generated content
 *
 * Special Cases Handled:
 * - "privileged_and_confidential": Summary for host-only
 * - "server_error": Display server issue notice
 * - "short_meeting": Show warning for meetings too short to summarize
 * - "not_enough_audio": Show warning about missing conversation audio
 * - Meeting topics not approved: Link to internal documentation
 *
 * Props:
 * - `summary` (object): Required. Meeting summary metadata, narrative, decisions, and next steps.
 * - isTopicNotApproved (boolean): Indicates if the meeting topic is not approved
 */
const SummaryBodyInDetailPage = ({ summary, isTopicNotApproved }) => {
  // Message for special cases like P&C, no audio case etc
  const SummaryMessage = ({ icon, color, children }) => (
    <Row width="100%" widths={["16px", "8px", "fill"]} wrap="none" alignmentHorizontal="start" spacing="none" alignmentVertical="top">
      <Icon tokens={icon} color={color} />
      <Row width="8px" />
      <Box>
        <Text type="b100" fontFamily="amazonEmber">
          {children}
        </Text>
      </Box>
    </Row>
  );

  const showOrganizerOnly = summary.summaryType === "privileged_and_confidential" && summary.role !== "host";
  const isServerError = summary.summaryType === "server_error";
  const isTooShort = summary.summaryType === "short_meeting";
  const isNotEnoughAudio = summary.summaryType === "not_enough_transcript";
  
  // TODO: Temporary workaround - detect reviewer restriction from summaryHighlight content, need to change in the future
  const isNotReviewer = summary.summaryHighlight && 
    (summary.summaryHighlight.includes("Contact") && summary.summaryHighlight.includes("if you want to see the meeting summary")) &&
    (summary.markdownSummary === null && summary.narrativeSummary === null && summary.decisions === null && summary.nextSteps === null);

  const handleLearnMoreClick = async () => {
    try {
      await zoomSdk.openUrl({ url: "https://w.amazon.com/bin/view/UnifiedCommunications/MeetingSummaries/AiMS" });
    } catch (error) {
      LogManager.error("Failed to open URL in Zoom:", error);
    }
  };

  // Check if this is a special case that should show error message instead of full summary
  const isSpecialCase = showOrganizerOnly || isServerError || isTopicNotApproved || isTooShort || isNotEnoughAudio || isNotReviewer;
  
  if (isSpecialCase) {
    const iconType = (showOrganizerOnly || isNotReviewer) ? alertFilledTokens : errorKnockout;
    const iconColor = (showOrganizerOnly || isNotReviewer) ? "warning" : "error";
    
    return (
      <Column spacing="none">
        <SummaryMessage icon={iconType} color={iconColor}>
          {summary.summaryHighlight}
        </SummaryMessage>
        {isTopicNotApproved && (
          <>
            <Column height="16px" />
            <Link onClick={handleLearnMoreClick} type="secondary">
              <Theme tokens={{ textColorPrimary: "#006D5B" }}>
                <Text color="primary" type="b200">
                  <u>Learn more</u>
                </Text>
              </Theme>
            </Link>
          </>
        )}
      </Column>
    );
  }

  return (
    <>
      {summary.summaryType === "privileged_and_confidential" && summary.role === "host" && (
        <Column spacing="none">
          <SummaryMessage icon={alertFilledTokens} color="warning">
            [Privileged and Confidential]
            <br />
            As the meeting organizer, you are the only participant who received this meeting summary. Seek approval from your legal counsel before
            sharing the meeting summary with employees outside the original attendee list.
          </SummaryMessage>
          <Column height="24px" />
        </Column>
      )}

      {/* Show Markdown Summary if available, otherwise show narrative Summary */}
      {summary.markdownSummary ? (
        <Box>
          <Text type="b100" fontFamily="amazonEmber">
            <ReactMarkdown 
              remarkPlugins={[remarkBreaks]}
              rehypePlugins={[rehypeRaw]}
            >
              {summary.markdownSummary.replace(/\n/gi, "&nbsp; \n")}
            </ReactMarkdown>
          </Text>
        </Box>
      ) : (
        <Box>
          <Text type="b100" color="secondary" fontFamily="amazonEmber">
            {summary.narrativeSummary}
          </Text>
        </Box>
      )}

      {/* Decisions and Next Steps Section */}
      <Column height="24px" />
      <Column spacing="none" spacingInset="none">
        {/* Decisions Box*/}
        <Box>
          <Column spacing="none" spacingInset="none">
            {/*Decisions title*/}
            <Row spacing="none" spacingInset="none">
              <Row width="8px">
                <img src={lineIcon} alt="lineIcon" style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }} />
              </Row>
              <Text type="h50" fontFamily="amazonEmber">
                Decisions
              </Text>
            </Row>

            <Column height="16px" />
            {/*Decisions bullet points */}
            <Box spacing="none" spacingInset="none">
              <ul style={{ paddingLeft: "20px", margin: 0 }}>
                {summary.decisions &&
                  summary.decisions.map((decision, index) => (
                    <li key={index}>
                      <Text type="b100" fontFamily="amazonEmber">
                        {decision}
                      </Text>
                    </li>
                  ))}
              </ul>
            </Box>

            <Column height="24px" />

            {/* Next Steps title */}
            <Row spacing="none" spacingInset="none">
              <Row width="8px">
                <img src={lineIcon} alt="lineIcon" style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }} />
              </Row>
              <Text type="h50" fontFamily="amazonEmber">
                Next Steps
              </Text>
            </Row>

            {/* Next Steps bullet points */}
            <Column height="16px" />

            <Box spacing="none" spacingInset="none">
              <ul style={{ paddingLeft: "20px", margin: 0 }}>
                {summary.nextSteps &&
                  summary.nextSteps.map((decision, index) => (
                    <li key={index}>
                      <Text type="b100" fontFamily="amazonEmber">
                        {decision}
                      </Text>
                    </li>
                  ))}
              </ul>
            </Box>
          </Column>
        </Box>
      </Column>

      <Column height="25px" />

      {/* AIMS Footer */}
      <Column spacing="none" spacingInset="none">
        <Theme tokens={{ textColorPrimary: "#575F67" }}>
          <Text fontFamily="amazonEmber" type="b100" color="primary">
            <b>Amazon Meetings Summary</b> is powered by Amazon AI. Always check for accuracy.
          </Text>
        </Theme>
      </Column>
    </>
  );
};

export default SummaryBodyInDetailPage;
